/**
 * Content → Pages: the text pages customers read — the privacy policy, terms, a returns policy —
 * each written once here and shown on the customer portal, the public website and the apps, and
 * listed in both sites' footers. Structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/roles-permissions/roles-grid.tsx (the
 * container-query card grid) and
 * vendor/isomorphic/apps/isomorphic/src/app/shared/roles-permissions/role-card.tsx (each card: the
 * coloured icon tile beside the name, the facts beneath, its actions).
 *
 * The privacy policy always has a card, written or not: the portal's footer and the website's
 * privacy.html link to it whatever exists, so it cannot be deleted here either.
 */
import type { ReactNode } from "react";
import { Link, useLoaderData, useRevalidator } from "react-router-dom";
import toast from "react-hot-toast";
import { Button, Text, Title } from "rizzui";
import {
  PiFileTextDuotone as PageIcon,
  PiPencilSimpleDuotone as EditIcon,
  PiPlusBold as Plus,
  PiShieldCheckDuotone as PolicyIcon,
  PiTrashDuotone as TrashIcon,
} from "react-icons/pi";
import { PageHeader, cn, getStatusBadge, problemTitle } from "@steelyard/theme";
import { useConfirm } from "../../components/confirm-modal";
import { formatMoment } from "../../lib/format";
import {
  PRIVACY_POLICY_SLUG,
  deleteContentPage,
  listContentPages,
  publicAddresses,
  type ContentPageSummary,
} from "../../lib/services/contentPages";

export async function contentPagesLoader() {
  return { pages: await listContentPages() };
}

export default function ContentPagesPage() {
  const { pages } = useLoaderData() as Awaited<ReturnType<typeof contentPagesLoader>>;
  const policy = pages.find((p) => p.slug === PRIVACY_POLICY_SLUG) ?? null;
  const others = pages.filter((p) => p.slug !== PRIVACY_POLICY_SLUG);

  return (
    <div className="flex flex-col gap-6 @container">
      <PageHeader title="Pages" breadcrumb={[{ name: "Home", href: "/" }, { name: "Content" }, { name: "Pages" }]} />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <Text className="max-w-3xl text-gray-500">
          The text pages your customers read. Publishing a page updates the customer portal, the website and the apps
          together, and lists it in both sites&apos; footers.
        </Text>
        <Link to="/content/pages/new">
          <Button as="span">
            <Plus aria-hidden className="me-1.5 size-4" />
            Add page
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-6 @[36.65rem]:grid-cols-2 @[56rem]:grid-cols-3">
        <PageCard
          slug={PRIVACY_POLICY_SLUG}
          title={policy?.title ?? "Privacy policy"}
          page={policy}
          icon={<PolicyIcon aria-hidden className="size-6" />}
          tile="bg-primary"
          note="What you collect from customers, why, who you share it with, and how they can ask you to change or delete it. Linked from both sites' footers and menus."
        />
        {others.map((page) => (
          <PageCard key={page.slug} slug={page.slug} title={page.title} page={page} icon={<PageIcon aria-hidden className="size-6" />} tile="bg-secondary" deletable />
        ))}
      </div>
    </div>
  );
}

function PageCard({
  slug,
  title,
  page,
  icon,
  tile,
  note,
  deletable = false,
}: {
  slug: string;
  title: string;
  page: ContentPageSummary | null;
  icon: ReactNode;
  tile: string;
  note?: string;
  deletable?: boolean;
}) {
  const confirm = useConfirm();
  const { revalidate } = useRevalidator();
  const addresses = publicAddresses(slug);

  const remove = () =>
    confirm({
      title: `Delete “${title}”?`,
      consequence: "It disappears from the customer portal, the website, the apps and both footers straight away. This cannot be undone.",
      confirmLabel: "Delete page",
      onConfirm: async () => {
        try {
          await deleteContentPage(slug);
          toast.success(`“${title}” deleted`);
          revalidate();
        } catch (err) {
          toast.error(problemTitle(err, "Could not delete this page"));
        }
      },
    });

  return (
    <div className="flex flex-col rounded-lg border border-muted p-6">
      <header className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-3">
          <span className={cn("grid size-10 shrink-0 place-content-center rounded-lg text-white", tile)}>{icon}</span>
          <Title as="h4" className="truncate font-medium">
            {title}
          </Title>
        </div>
        {getStatusBadge(page ? "published" : "not_published")}
      </header>

      {note ? <Text className="mt-4 text-sm text-gray-500">{note}</Text> : null}

      <dl className="mt-5 grid gap-3 text-sm">
        <div className="flex justify-between gap-3">
          <dt className="text-gray-500">Last published</dt>
          <dd className="text-end font-medium">{page ? formatMoment(page.updatedAt) : "Never"}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-gray-500">Customer portal</dt>
          <dd className="truncate text-end font-medium">{addresses.portal}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-gray-500">Website</dt>
          <dd className="truncate text-end font-medium">{addresses.website}</dd>
        </div>
      </dl>

      <div className="mt-auto flex gap-3 pt-6">
        <Link to={`/content/pages/${slug}`} className="block flex-1">
          <Button as="span" className="w-full">
            <EditIcon aria-hidden className="me-1.5 size-4" />
            {page ? "Edit page" : "Write this page"}
          </Button>
        </Link>
        {deletable ? (
          <Button variant="outline" color="danger" onClick={remove} aria-label={`Delete ${title}`}>
            <TrashIcon aria-hidden className="size-4" />
          </Button>
        ) : null}
      </div>
    </div>
  );
}
